import { cardFeatures } from '../../../../shared/trios';
import type { ui as en } from '../en/ui';
import { symbols as snapSymbols } from './snap';

export const ui: typeof en = {
  chat: {
    title: '聊天',
    message: '消息',
    placeholder: '猜一猜，或打个招呼…',
    send: '发送',
    unread: (tab, count) => `${tab}，${count} 条新消息`,
  },
  you: '你',
  yourTurn: '轮到你',
  turnTab: (tab) => `${tab}，轮到你了`,
  youName: (name) => `${name}（你）`,
  host: '房主',
  players: '玩家',
  seatsTaken: (players, seats) => `已有 ${players} 人，共 ${seats} 个位置`,
  turn: '当前回合',
  standings: {
    entry: (name, value) => `${name} ${value}`,
    label: (entries) => `排名：${entries.join('，')}。查看玩家`,
    away: '暂离',
    guesses: (used, most) => `${used}/${most}`,
    mark: { solved: '已猜中', missed: '未猜中' },
  },
  fullRules: '完整规则',
  hideFullRules: '收起完整规则',
  room: {
    leave: '离开房间',
    howToPlay: '玩法',
    contextSeparator: ' · ',
    password: '需要密码',
    players: (players, seats) => `${players} / ${seats} 位玩家`,
    full: '已满',
    playing: '游戏中',
    join: '加入',
    watch: '观战',
  },
  drawing: {
    canvas: '绘画画布',
    image: '当前画作',
    tools: '绘图工具',
    colour: '颜色',
    size: '画笔大小',
    undo: '撤销',
    clear: '清空',
    pixels: (width) => `${width} 像素`,
    colours: {
      ink: '墨黑',
      graphite: '石墨灰',
      white: '白色',
      coral: '珊瑚色',
      red: '红色',
      orange: '橙色',
      sun: '黄色',
      leaf: '黄绿色',
      green: '绿色',
      sky: '天蓝色',
      violet: '紫色',
      brown: '棕色',
    },
  },
  chain: {
    suggest: '换个点子',
    done: '完成',
    keepEditing: '继续修改',
    writeFirst: '先写点什么，再按“完成”。',
    heart: (count) => `送一颗爱心，已有 ${count} 颗`,
    hearts: (count) => `${count} 颗爱心`,
  },
  dice: {
    title: '骰子',
    hidden: '未揭开的骰子',
    lost: '已失去的骰子',
    wild: (face) => `${face} 点，百搭`,
    counts: (face) => `${face} 点，计入叫点`,
    bid: (name, count, face) => `${name}：${count} 颗 ${face} 点`,
    picker: '你的叫点',
    howMany: '数量',
    fewer: '减少',
    more: '增加',
    showing: '点数',
    call: '喊“吹牛”',
    face: (face) => `${face} 点`,
  },
  word: {
    keyboard: '键盘',
    enter: '确认',
    delete: '删除字母',
    marks: {
      correct: '位置正确',
      present: '在单词中，但位置不对',
      absent: '不在单词中',
    },
    marked: (letter, mark) => `${letter}，${mark}`,
    board: (name) => `${name} 的猜词板`,
    letters: (count) => `${count} 个字母`,
  },
  code: {
    pad: '数字键盘',
    notes: '笔记',
    delete: '删除数字',
    noted: (digit, note) =>
      `${digit}，已记为${note === 'out' ? '不在答案中' : '在答案中'}`,
    ruledOut: (digit) => `${digit}，不在答案中`,
    mark: (right, moved) => `${right} 个位置正确，${moved} 个位置不对`,
    board: (name) => `${name} 的猜数板`,
  },
  lives: (lives) => (lives === 0 ? '没有命了' : `${lives} 条命`),
  discarded: (value) => `${value}，已弃置`,
  position: (row, column, detail) => `第 ${row} 行，第 ${column} 列：${detail}`,
  mine: {
    hidden: '未揭开',
    picked: '你的选择',
    mine: '地雷',
    flag: '已标旗',
    wrongFlag: '已标旗，没有地雷',
    hit: '你踩中的地雷',
    empty: '空白',
    adjacent: (count) => `周围有 ${count} 个地雷`,
  },
  outcome: { safe: '安全', mine: '踩雷', auto: '自动选择' },
  pairs: {
    down: '背面朝上',
    matched: (symbol) => `${symbol}，已配对`,
    symbols: [
      '珊瑚色圆形',
      '天蓝色圆环',
      '黄色半圆',
      '黄绿色方形',
      '紫色菱形',
      '绿色三角形',
      '橙色星形',
      '红色闪光',
      '棕色六边形',
      '紫色圆形',
      '绿色圆环',
      '橙色半圆',
      '红色方形',
      '棕色菱形',
      '珊瑚色三角形',
      '天蓝色星形',
      '黄色闪光',
      '黄绿色六边形',
    ],
  },
  trios: {
    card: (card) => {
      const { colour, shape, count, fill } = cardFeatures(card);
      return `${count} 个${{ coral: '珊瑚色', blue: '蓝色', ink: '黑色' }[colour]}${{ solid: '实心', striped: '条纹', outline: '空心' }[fill]}${{ circle: '圆形', triangle: '三角形', square: '方形' }[shape]}`;
    },
    states: {
      selected: '，已选中',
      found: '，已找到',
      wrong: '，不成一组',
    },
    badge: (name, badge) => `${name}，${badge}`,
  },
  snap: {
    symbol: (symbol) => snapSymbols[symbol] ?? '',
    marks: { match: '，两张牌都有', miss: '，不是这个' },
    note: (name, note) => `${name}，${note}`,
    takenBy: (who) => `被 ${who} 抢走`,
    locked: '锁定中',
  },
  gomoku: {
    point: (name, stone, { last, won }) =>
      [
        name,
        stone === null ? '空' : stone === 'black' ? '黑子' : '白子',
        won ? '连成五子' : last ? '最后一手' : null,
      ]
        .filter(Boolean)
        .join('，'),
  },
};
